import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import UserAvatar from "../../common/UserAvatar";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FilledButton } from "../../customComponents/styled/styledButtons";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import NotificationsCard from "./NotificationsCard";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../auth/authSlice";
import {
  useGetProfileQuery,
  useGetUserProfileQuery,
} from "./dashboardApiSlice";
import { useGetTeacherSubjectsQuery } from "../staff/staffApiSlice";
import { useGetClassesQuery } from "../classroom/classApiSlice";

function Dashboard() {
  const user = useSelector(selectCurrentUser);
  const navigate = useNavigate();
  const isTeacher = user?.roles?.some((r) => r?.role?.name === "teacher");
  const isAgent = user?.roles?.some((r) => r?.role?.name === "agent");
  const isReviewer = user?.roles?.some((r) => r?.role?.name === "reviewer");
  const isAdminLike = user?.roles?.some((r) =>
    ["admin", "examiner", "accountant", "webmaster"].includes(r?.role?.name),
  );
  const { data: teacherProfile } = useGetProfileQuery(undefined, {
    skip: !isTeacher,
  });
  const { data: userProfile } = useGetUserProfileQuery(undefined, {
    skip: isTeacher,
  });
  const profile = isTeacher ? teacherProfile : userProfile;

  const [showAgentPhoneModal, setShowAgentPhoneModal] = useState(false);

  useEffect(() => {
    if (
      isAgent &&
      profile &&
      (!profile?.phone || !String(profile.phone).trim())
    ) {
      setShowAgentPhoneModal(true);
    }
  }, [isAgent, profile]);

  const handleContinuePhoneUpdate = () => {
    setShowAgentPhoneModal(false);
    navigate("edit-profile");
  };

  const { data: teacherSubjects, isSuccess: subjectsLoaded } =
    useGetTeacherSubjectsQuery(undefined, { skip: !isTeacher || !isReviewer });
  const { data: myClasses, isSuccess: classesLoaded } = useGetClassesQuery(
    "all/mine",
    { skip: !isTeacher || !isReviewer },
  );

  useEffect(() => {
    if (!isTeacher || !isReviewer || !subjectsLoaded || !classesLoaded) return;
    if (isAdminLike) return;
    if (!teacherSubjects?.length && !myClasses?.length) {
      navigate("/lms/overview", { replace: true });
    }
  }, [
    isTeacher,
    isReviewer,
    isAdminLike,
    teacherSubjects,
    myClasses,
    subjectsLoaded,
    classesLoaded,
    navigate,
  ]);
  return (
    <>
      <Grid container columnSpacing={4}>
        <Grid item xs={12} md={4}>
          <StyledPaper>
            <Box sx={{ padding: "30px 0" }}>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <UserAvatar
                  src={profile?.photo}
                  name={`${profile?.first_name || ""} ${profile?.last_name || ""}`.trim()}
                  sx={{ width: 120, height: 120, fontSize: "2.5rem" }}
                />
              </Box>
              <Box sx={{ marginTop: "20px", textAlign: "center" }}>
                <Typography
                  sx={{
                    color: "#5a7391",
                    fontSize: "20px",
                    fontWeight: 600,
                    marginBottom: "7px",
                    textTransform: "capitalize",
                  }}
                >
                  Welcome, {user?.profile?.first_name}{" "}
                  {user?.profile?.last_name}
                </Typography>
                <Link to="edit-profile">
                  <FilledButton sx={{ marginTop: "10px" }}>
                    Edit Profile
                  </FilledButton>
                </Link>
                {user?.roles?.some((r) => r?.role?.name === "parent") && (
                  <Link to="/parent">
                    <FilledButton sx={{ marginTop: "10px", ml: 1 }}>
                      Parent Portal
                    </FilledButton>
                  </Link>
                )}
              </Box>

              <Box
                sx={{
                  marginTop: "30px",
                  "& .navItem": {
                    fontSize: "16px",
                    fontWeight: 400,
                    display: "block",
                    padding: "10px 15px",
                    textTransform: "capitalize",
                    color: "#93a3b5",
                    cursor: "pointer",
                  },
                  "& .navItem.active": {
                    color: "primary.main",
                    backgroundColor: "primary.transparent",
                    borderLeft: "2px solid",
                    borderLeftColor: "primary.main",
                    marginLeft: "-2px",
                  },
                  "& .navItem:hover": {
                    backgroundColor: "#f3f6fa",
                  },
                }}
              >
                <Typography className="navItem active">my activity</Typography>
              </Box>
            </Box>
          </StyledPaper>
        </Grid>
        <Grid item xs={12} md={8}>
          <StyledPaper>
            <NotificationsCard />
          </StyledPaper>
        </Grid>
      </Grid>

      <Dialog
        open={showAgentPhoneModal}
        onClose={() => setShowAgentPhoneModal(false)}
        disableEscapeKeyDown
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Update Contact Phone Number
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Please update your contact phone number to complete your agent
            profile details.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleContinuePhoneUpdate}
            sx={{ textTransform: "none" }}
          >
            Continue to Edit Profile
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Dashboard;
